import React, { useState } from "react"
import { Grid, LinearProgress, Tooltip, Typography } from "@material-ui/core"
import { InfoOutlined as InfoIcon, OpenInNew as OpenVoiceReportIcon } from "@material-ui/icons"
import { Link } from "react-router-dom"

import { Divider, Portlet } from "@/components"

import { VoiceCallUsageSummary } from "@/protocols/usage"

import useCustomStyles from "@/styles/custom"
import useStyles from "@/pages/Admin/Dashboard/Metrics/VoiceCallUsageMetrics/styles"

import useDidMount from "@/hooks/useDidMount"
import ErrorHandlerService from "@/services/ErrorHandler"
import UsageService from "@/services/Usage"

import { turnBilledDurationSecondsInCredits } from "@/utils/Voice"
import { getFormattedFullWatchTime } from "@/utils/time"

const MAX_PROGRESS_PERCENTAGE = 100
const VOICE_CALL_REPORT_PATH = "/admin/report/voice-call-report"

const VoiceCallUsageMetrics: React.FC = () => {
	const customClasses = useCustomStyles()
	const classes = useStyles()

	const [usageSummary, setUsageSummary] = useState<VoiceCallUsageSummary | null>(null)
	const [loading, setLoading] = useState(true)

	const hasNegativeBalance = usageSummary?.balance === "negative"

	const getUsageSummary = async () => {
		setLoading(true)

		try {
			const summary = await UsageService.getUsageSummary("voice-call")

			setUsageSummary(summary)
		} catch (error) {
			ErrorHandlerService.handle(error as Error)
		}

		setLoading(false)
	}

	useDidMount(() => {
		getUsageSummary()
	})

	if (loading || !usageSummary) {
		return null
	}

	const progressValue = Math.min(usageSummary.percentageUsed, MAX_PROGRESS_PERCENTAGE)
	const formattedDuration = getFormattedFullWatchTime(usageSummary.secondsUsed)

	return (
		<Grid container>
			<Grid item xs={12}>
				<Grid container className={classes.constainerDivision}>
					<Grid item>
						<Typography
							variant="h2"
							color="textPrimary"
							style={{ fontSize: 18 }}
							className={customClasses.uppercase}
						>
							CRÉDITOS DE LIGAÇÃO
						</Typography>
					</Grid>
				</Grid>
			</Grid>

			<Divider orientation="horizontal" size={2} />

			<Grid item style={{ width: "100%" }}>
				<Portlet>
					<Grid container spacing={2}>
						<Grid item xs={12}>
							<Grid container justifyContent="space-between" alignItems="center">
								<Grid item>
									<Typography variant="body2" color="textSecondary">
										Créditos consumidos
									</Typography>
								</Grid>

								<Grid item>
									<Typography variant="body2" color={hasNegativeBalance ? "error" : "textSecondary"}>
										{`${usageSummary.percentageUsed}%`}
									</Typography>
								</Grid>
							</Grid>
						</Grid>

						<Grid item xs={12}>
							<LinearProgress
								variant="determinate"
								value={progressValue}
								classes={{
									root: classes.progressRoot,
									colorPrimary: hasNegativeBalance ? classes.progressTrackNegative : classes.progressTrack,
									bar: hasNegativeBalance ? classes.progressBarNegative : classes.progressBar
								}}
							/>
						</Grid>

						<Grid item xs={12}>
							<Grid container justifyContent="space-between" alignItems="center">
								<Grid item>
									<Grid container alignItems="center" spacing={1}>
										<Grid item>
											<Typography variant="caption" color="textSecondary">
												{`${turnBilledDurationSecondsInCredits(usageSummary.secondsUsed)} de ${usageSummary.minutesLimit} créditos`}
											</Typography>
										</Grid>

										<Tooltip title={`Essa quantidade de créditos consumida equivale a um tempo total de ligação de ${formattedDuration}.`}>
											<InfoIcon className={classes.infoIcon} />
										</Tooltip>
									</Grid>
								</Grid>

								<Grid item>
									<Link to={VOICE_CALL_REPORT_PATH} className={customClasses.hyperlink}>
										<Typography variant="caption" color="inherit">
											Ver relatório de ligações
										</Typography>
										<OpenVoiceReportIcon className={classes.linkIcon} />
									</Link>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				</Portlet>
			</Grid>
		</Grid>
	)
}

export default VoiceCallUsageMetrics
